import React, { useCallback, useEffect, useRef } from 'react';
import { flowRight } from 'lodash';
import { type ConnectedProps } from 'react-redux';
import { SlotsPlaceholder } from '@wix/widget-plugins-ooi';
import { type WithTranslation, withTranslation } from '@wix/yoshi-flow-editor';
import {
  createPageUrl,
  ENTITY_TYPE_POSTS,
  getAppConfig,
  isLayoutProGallery,
  resolveId,
  resolveLegacyId,
  SECTION_CATEGORY,
  type LayoutType,
  feedWidgetStyleParams,
  getCategoryUrl,
} from '@wix/communities-blog-client-common';
import stylesParams from '@app/components/Blog/stylesParams';
import { PostTitleTagProvider } from '@app/external/common/context/post-title-tag-context';
import withDeviceType, { type WithDeviceType } from '@app/external/common/hoc/with-device-type';
import { ABOVE_POST_LIST } from '../../../../constants/ooi-slots';
import AnimatedLoader from '../../../common/components/animated-loader';
import withComponents, {
  type WithComponents,
} from '../../../common/components/components-provider/with-components';
import { EmptyLayout } from '../../../common/components/layout';
import Page from '../../../common/components/page';
import { connect } from '../../../common/components/runtime-context';
import Pagination from '../../../common/containers/pagination';
import withAuth, { type WithAuth } from '../../../common/hoc/with-auth';
import withLayoutProps, { type WithLayoutProps } from '../../../common/hoc/with-layout-props';
import withPermissions, { type WithPermissions } from '../../../common/hoc/with-permissions';
import { getAppSettingsValue } from '../../../common/selectors/app-settings-base-selectors';
import {
  getCanSeeMoreButton,
  getIsCategoryHeaderEnabled,
} from '../../../common/selectors/app-settings-selectors';
import {
  isExcludePostContentSupported,
  isPostContentRequired,
} from '../../../common/selectors/layout-selectors';
import {
  getPostsByCategoryIdAndPage,
  getSortedPostsByCategoryId,
} from '../../../common/selectors/post-selectors';
import { handleNavigation } from '../../../common/services/navigation';
import { resolveCategorySlug } from '../../../common/services/slug';
import { isSite as getIsSite } from '../../../common/store/basic-params/basic-params-selectors';
import { getCategoryBySlug } from '../../../common/store/categories/categories-selectors';
import { getIsCategoryLoaded } from '../../../common/store/is-loaded/is-loaded-selectors';
import { getIsCategoryLoading } from '../../../common/store/is-loading/is-loading-selectors';
import { getSectionUrl } from '../../../common/store/topology/topology-selectors';
import CategoryHeader from '../../components/category-header';
import FeedLayoutBox from '../../components/feed-layout-box/feed-layout-box';
import NoPosts from '../../components/no-posts-writer';
import PostList from '../../components/post-list';
import { type RouteCategoryParams } from '../../constants/routes';
import withPaginationSettings, {
  type WithPaginationSettings,
} from '../../hoc/with-pagination-settings';

type OwnProps = {
  params: RouteCategoryParams;
};

type Props = OwnProps &
  WithComponents &
  WithPermissions &
  WithPaginationSettings &
  WithLayoutProps &
  WithAuth &
  WithTranslation &
  WithDeviceType &
  ConnectedProps<typeof connector>;

const Layout = EmptyLayout;

const CategoryPage: React.FC<Props> = (props) => {
  const {
    currentPagePosts,
    allPosts,
    entityCount,
    category,
    isCategoryLoading,
    isCategoryLoaded,
    page,
    layoutType,
    showPagination,
    PostListProGallery,
    canSeeMoreButton,
    pageSize,
    isSite,
    categoryPath,
    categoryUrl,
    isMobile,
    isLegacyCategoryHeaderEnabled,
  } = props;
  // Read by the stable loadMore/loadRest and the unmount cleanup, so must be the latest render's
  const latest = useRef(props);
  latest.current = props;
  const prev = useRef<Props>();

  const fetchPosts = (categoryId: string, page = latest.current.page): Promise<any> => {
    if (latest.current.isBlocked) {
      return Promise.resolve();
    }

    return latest.current.fetchCategoryPosts({
      categoryId,
      page,
      excludeContent: latest.current.excludeContent,
    });
  };

  useEffect(() => {
    const prevProps = prev.current;
    prev.current = props;
    if (!prevProps) {
      return;
    }

    if (
      props.isAuthenticated !== prevProps.isAuthenticated ||
      props.isBlocked !== prevProps.isBlocked ||
      props.pageSize !== prevProps.pageSize ||
      (prevProps.excludeContent && !props.excludeContent)
    ) {
      fetchPosts(resolveId(category));
    }

    if (props.params.categorySlug !== prevProps.params.categorySlug) {
      props.paginationClearCollection({ entityType: ENTITY_TYPE_POSTS });
    }
  });

  useEffect(
    () => () => latest.current.setIsLoaded('category', resolveId(latest.current.category), false),
    [],
  );

  const loadMore = useCallback(
    (page: number) => fetchPosts(resolveId(latest.current.category), page),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const loadRest = useCallback(
    () => fetchPosts(resolveId(latest.current.category), latest.current.page),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Stable identity: Pagination's memoized createPageLink remounts every link when it changes
  const createCategoryPageUrl = useCallback(
    (page: number) => createPageUrl(page, categoryUrl),
    [categoryUrl],
  );

  const renderPosts = () => {
    if (allPosts.length === 0 && isCategoryLoaded && category) {
      return <NoPosts />;
    }

    if (isLayoutProGallery(layoutType as LayoutType)) {
      return (
        <PostListProGallery
          pageStart={page + 1}
          entityCount={entityCount}
          loadMore={showPagination ? undefined : loadMore}
          allPosts={showPagination ? currentPagePosts : allPosts}
          currentPagePosts={currentPagePosts}
          isLoading={isCategoryLoading}
          canSeeMoreButton={canSeeMoreButton}
          layoutOptions={props.layoutOptions}
          section={SECTION_CATEGORY}
        />
      );
    }

    return (
      <PostList
        pageStart={page + 1}
        layoutType={layoutType}
        entityCount={entityCount}
        page={page}
        pageSize={pageSize}
        loadRest={showPagination ? loadRest : undefined}
        loadMore={showPagination ? undefined : loadMore}
        allPosts={showPagination ? currentPagePosts : allPosts}
        currentPagePosts={currentPagePosts}
        isLoading={isCategoryLoading}
      />
    );
  };

  const isLoading = isCategoryLoading && !isCategoryLoaded;

  return (
    <PostTitleTagProvider titleFontParam={stylesParams.cssTitleFontCategory} fallbackTagName="h2">
      <Page noSpacing>
        <Layout>
          <CategoryHeader category={category} legacyEnabled={isLegacyCategoryHeaderEnabled} />
          <FeedLayoutBox noPadding={isMobile}>
            <SlotsPlaceholder slotId={ABOVE_POST_LIST} />
          </FeedLayoutBox>
          <AnimatedLoader isLoading={isLoading && !allPosts.length}>{renderPosts()}</AnimatedLoader>
          {!isLoading && showPagination ? (
            <div data-hook="category-pagination-container" style={{ display: 'block' }}>
              <Pagination
                dataHook="category-pagination-container"
                page={page}
                pageSize={pageSize}
                entityCount={entityCount}
                createPageUrl={createCategoryPageUrl}
                handleNavigation={handleNavigation(categoryPath, isSite)}
              />
            </div>
          ) : null}
        </Layout>
      </Page>
    </PostTitleTagProvider>
  );
};

const connector = connect(
  (state, ownProps: OwnProps & WithPermissions & WithPaginationSettings, actions) => {
    const categorySlug = resolveCategorySlug(ownProps.params);
    const category = getCategoryBySlug(state, categorySlug);
    const categoryId = resolveId(category);
    const mixedCategoryId = resolveLegacyId(category) ?? categoryId;

    const currentPagePosts = mixedCategoryId
      ? getPostsByCategoryIdAndPage(state, mixedCategoryId, ownProps.page)
      : [];
    const allPosts = mixedCategoryId ? getSortedPostsByCategoryId(state, mixedCategoryId) : [];
    const { categoryPath, useCategoryMenuLabelForMetadataTitle } = getAppConfig(state);
    const sectionUrl = getSectionUrl(state);
    const categoryUrl = getCategoryUrl(sectionUrl, categoryPath ?? '', categorySlug);

    const {
      feedSliderShowArrows,
      feedSliderArrowsSize,
      feedSliderAutoSlide,
      feedSliderPauseTime,
      feedSliderArrowsPosition,
      feedSliderArrowsColor,
      feedSliderLoop,
    } = feedWidgetStyleParams;

    return {
      categoryUrl,
      categoryPath: `/${categoryPath}/${categorySlug}`,
      category,
      allPosts,
      currentPagePosts,
      categoryId,
      isCategoryLoading: getIsCategoryLoading(state, categoryId),
      // Deprecated app setting from the pre-plugin header. Sites that had it on
      // keep the original header here, unchanged, until the
      // `categoryHeader-show` style param takes over.
      isLegacyCategoryHeaderEnabled: getIsCategoryHeaderEnabled(state),
      isCategoryLoaded: getIsCategoryLoaded(state, categoryId),
      isSite: getIsSite(state),
      excludeContent: isExcludePostContentSupported(state),
      useCategoryMenuLabelForMetadataTitle,
      setIsLoaded: actions.setIsLoaded,
      fetchCategoryPosts: (params: {
        page: number;
        categoryId: string;
        excludeContent: boolean;
      }) => {
        return actions.fetchCategoryPostsWithAdapterPromisified({
          page: params.page,
          includeContent: isPostContentRequired(state),
          categoryOptions: { id: params.categoryId },
        });
      },
      paginationClearCollection: actions.paginationClearCollection,
      canSeeMoreButton: getCanSeeMoreButton(state, ownProps.canSee),
      layoutOptions: {
        showArrows: getAppSettingsValue({
          state,
          key: `style.booleans.${feedSliderShowArrows.key}`,
          fallback: feedSliderShowArrows.defaultValue,
        }),
        arrowsSize: getAppSettingsValue({
          state,
          key: `style.numbers.${feedSliderArrowsSize.key}`,
          fallback: feedSliderArrowsSize.defaultValue,
        }),
        autoSlide: getAppSettingsValue({
          state,
          key: `style.booleans.${feedSliderAutoSlide.key}`,
          fallback: feedSliderAutoSlide.defaultValue,
        }),
        pauseTime: getAppSettingsValue({
          state,
          key: `style.numbers.${feedSliderPauseTime.key}`,
          fallback: feedSliderPauseTime.defaultValue,
        }),
        arrowsPosition: getAppSettingsValue({
          state,
          key: `style.numbers.${feedSliderArrowsPosition.key}`,
          fallback: feedSliderArrowsPosition.defaultValue,
        }),
        arrowsColor: getAppSettingsValue({
          state,
          key: `style.colors.${feedSliderArrowsColor.key}`,
          fallback: feedSliderArrowsColor.defaultValue,
        }),
        loop: getAppSettingsValue({
          state,
          key: `style.booleans.${feedSliderLoop.key}`,
          fallback: feedSliderLoop.defaultValue,
        }),
      },
    };
  },
);

export default flowRight(
  withComponents,
  withPermissions,
  withPaginationSettings(SECTION_CATEGORY),
  withLayoutProps(),
  withAuth,
  withTranslation(),
  withDeviceType,
  connector,
)(CategoryPage);
